import { useState, useMemo } from "react";
import { useNavigate } from "react-router";
import { Search, Plus, ArrowUpDown, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "@/components/ui/table";
import { useBiometrics, useBiometricPrices, useBloodTestPackages, useCreateBiometric } from "@/hooks/api";
import type { Biometric } from "@/lib/api_client";

type SortKey = "name" | "nlk" | "furstId" | "unit";
type SortDir = "asc" | "desc";

export function BiometricsList() {
  const { data: biometrics, isLoading } = useBiometrics();
  const { data: prices } = useBiometricPrices();
  const { data: packages } = useBloodTestPackages();
  const createBiometric = useCreateBiometric();
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("name");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    let list = biometrics ?? [];
    if (q) {
      list = list.filter(
        (b) =>
          b.name.toLowerCase().includes(q) ||
          b.shortName?.toLowerCase().includes(q) ||
          b.nlk?.toLowerCase().includes(q) ||
          b.furstId?.toLowerCase().includes(q)
      );
    }
    list = [...list].sort((a, b) => {
      const aVal = (a[sortKey] ?? "").toLowerCase();
      const bVal = (b[sortKey] ?? "").toLowerCase();
      return sortDir === "asc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    });
    return list;
  }, [biometrics, search, sortKey, sortDir]);

  const handleCreate = async () => {
    const bio = await createBiometric.mutateAsync({
      name: "Ny biomarkor",
    });
    navigate(`/biometrics/${bio.id}`);
  };

  const getPricesForBio = (bioId: string) => (prices ?? []).filter((p) => p.biometricId === bioId);
  const getPackagesForBio = (bioId: string) => (packages ?? []).filter((p) => (p.biometricIds as string[] | undefined)?.includes(bioId));

  const SortButton = ({ col, children }: { col: SortKey; children: React.ReactNode }) => (
    <button
      onClick={() => toggleSort(col)}
      className="flex items-center gap-1 hover:text-admin-text-primary transition-colors cursor-pointer"
    >
      {children}
      <ArrowUpDown className="h-3 w-3" />
    </button>
  );

  if (isLoading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-admin-text-subtle" /></div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={handleCreate} size="sm" disabled={createBiometric.isPending}>
          <Plus className="h-4 w-4" />
          Opprett ny
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-admin-text-subtle" />
        <Input
          placeholder="Sok etter biomarkor..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      <div className="rounded-lg border border-admin-card-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-admin-card">
              <TableHead><SortButton col="name">Navn</SortButton></TableHead>
              <TableHead><SortButton col="nlk">NLK</SortButton></TableHead>
              <TableHead><SortButton col="furstId">Furst ID</SortButton></TableHead>
              <TableHead><SortButton col="unit">Enhet</SortButton></TableHead>
              <TableHead>Pris</TableHead>
              <TableHead>Pakker</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((bio) => {
              const bioPrices = getPricesForBio(bio.id);
              const bioPackages = getPackagesForBio(bio.id);
              return (
                <TableRow
                  key={bio.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/biometrics/${bio.id}`)}
                >
                  <TableCell>
                    <div>
                      <span className="font-medium">{bio.name}</span>
                      {bio.shortName && (
                        <span className="block text-xs text-admin-text-subtle">{bio.shortName}</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-admin-text-secondary font-mono text-xs">
                    {bio.nlk ?? "-"}
                  </TableCell>
                  <TableCell className="text-admin-text-secondary font-mono text-xs">
                    {bio.furstId ?? "-"}
                  </TableCell>
                  <TableCell className="text-admin-text-secondary">{bio.unit ?? "-"}</TableCell>
                  <TableCell>
                    {bioPrices.length > 0
                      ? bioPrices.map((p) => (
                          <span key={p.id} className="block text-xs">
                            {Number(p.price).toFixed(2)} kr ({p.source})
                          </span>
                        ))
                      : <span className="text-admin-text-subtle">-</span>}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {bioPackages.map((pkg) => (
                        <Badge key={pkg.id} variant="secondary">{pkg.name}</Badge>
                      ))}
                      {bioPackages.length === 0 && (
                        <span className="text-admin-text-subtle text-xs">-</span>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-admin-text-subtle py-8">
                  Ingen biomarkorer funnet
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
